import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { toast } from 'react-toastify';
import SendMessageModal from './SendMessageModal';
import ProfileModal from './ProfileModal';
import { getMemberInfo } from '../api/membersApi';
import { getPlans } from '../api/planApi';
import {
    completeAllWorkflowTasks,
    completeWorkflowTask,
    createWorkflowRule,
    deleteWorkflowRule,
    getWorkflowOverview,
    getWorkflowRuleTypes,
    getWorkflowTasks,
    setWorkflowActive,
    updateWorkflowRule,
} from '../api/workflowApi';

const PRIORITY_OPTIONS = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
];

const PRIORITY_COLORS = { high: 'error', medium: 'warning', low: 'success' };
const PRIORITY_WEIGHT = { high: 0, medium: 1, low: 2 };

const PARAM_LABELS = {
    days: 'Number of days',
    percent: 'Drop percentage (%)',
    visits: 'Number of visits',
    years: 'Number of years',
    planId: 'Plan',
};

// One-click starter rules shown in the empty state.
const SUGGESTED_RULES = [
    {
        type: 'inactive_member',
        name: 'Inactive for 21 days',
        params: { days: 21 },
        priority: 'high',
    },
    {
        type: 'new_member_onboarding',
        name: 'Welcome call after 7 days',
        params: { days: 7 },
        priority: 'medium',
    },
    {
        type: 'contract_ending',
        name: 'Contract ending in 14 days',
        params: { days: 14 },
        priority: 'high',
    },
];

const emptyRuleForm = {
    id: null,
    type: '',
    name: '',
    priority: 'medium',
    params: {},
};

// European date format: dd.mm.yyyy
const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
};

export default function WorkflowManagement({ affiliateId, affiliate, affiliateEmail }) {
    const [loading, setLoading] = useState(true);
    const [workflowActive, setWorkflowActiveState] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);
    const [rules, setRules] = useState([]);
    const [ruleTypes, setRuleTypes] = useState([]);
    const [plans, setPlans] = useState([]);
    const [activeTab, setActiveTab] = useState(0);

    // Tasks state
    const [tasks, setTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [ruleFilter, setRuleFilter] = useState('all');

    // Dialogs
    const [confirmTask, setConfirmTask] = useState(null);
    const [completingTask, setCompletingTask] = useState(false);
    const [confirmCompleteAll, setConfirmCompleteAll] = useState(false);
    const [completingAllTasks, setCompletingAllTasks] = useState(false);
    const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
    const [ruleForm, setRuleForm] = useState(emptyRuleForm);
    const [savingRule, setSavingRule] = useState(false);
    const [deleteRuleTarget, setDeleteRuleTarget] = useState(null);
    const [deletingRule, setDeletingRule] = useState(false);

    // Email modal
    const [emailTask, setEmailTask] = useState(null);

    // Member profile modal (same as ClassModal)
    const [selectedUser, setSelectedUser] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);

    const ruleTypeMap = useMemo(() => {
        const map = {};
        for (const rt of ruleTypes) map[rt.type] = rt;
        return map;
    }, [ruleTypes]);

    const loadOverview = useCallback(async () => {
        const overview = await getWorkflowOverview(affiliateId);
        setWorkflowActiveState(overview.workflowActive);
        setRules(overview.rules);
        setPendingCount(overview.pendingCount);
    }, [affiliateId]);

    const loadTasks = useCallback(
        async (completed = showCompleted) => {
            setTasksLoading(true);
            try {
                const data = await getWorkflowTasks(
                    affiliateId,
                    completed ? 'completed' : 'pending'
                );
                setTasks(data);
            } catch (error) {
                console.error('Error loading workflow tasks:', error);
                toast.error('Failed to load tasks');
            } finally {
                setTasksLoading(false);
            }
        },
        [affiliateId, showCompleted]
    );

    useEffect(() => {
        if (!affiliateId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [types, plansData] = await Promise.all([
                    getWorkflowRuleTypes(),
                    getPlans(affiliateId),
                ]);
                if (cancelled) return;
                setRuleTypes(types);
                setPlans(Array.isArray(plansData) ? plansData : []);
                await loadOverview();
                await loadTasks(false);
            } catch (error) {
                console.error('Error loading workflow data:', error);
                toast.error('Failed to load workflow data');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [affiliateId]);

    const handleToggleWorkflow = async (event) => {
        const active = event.target.checked;
        setWorkflowActiveState(active);
        try {
            await setWorkflowActive(affiliateId, active);
            toast.success(active ? 'Workflow activated' : 'Workflow deactivated');
        } catch (error) {
            console.error('Error toggling workflow:', error);
            setWorkflowActiveState(!active);
            toast.error('Failed to update workflow state');
        }
    };

    const handleShowCompletedToggle = async (event) => {
        const completed = event.target.checked;
        setShowCompleted(completed);
        await loadTasks(completed);
    };

    const handleCopyPhone = async (phone) => {
        try {
            await navigator.clipboard.writeText(phone);
            toast.success('Phone number copied');
        } catch (error) {
            console.error('Clipboard error:', error);
            toast.error('Failed to copy');
        }
    };

    const handleOpenProfile = async (userId) => {
        if (!userId) return;
        try {
            const userData = await getMemberInfo(userId, affiliateId);
            if (!userData) {
                toast.error('Could not load member profile');
                return;
            }
            setSelectedUser(userData);
            setProfileOpen(true);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            toast.error('Could not load member profile');
        }
    };

    const handleUserUpdated = async (userId) => {
        try {
            const userData = await getMemberInfo(userId, affiliateId);
            if (userData) setSelectedUser(userData);
        } catch (error) {
            console.error('Error updating user profile:', error);
            toast.error('Failed to update user data');
        }
    };

    const handleCompleteTask = async () => {
        if (!confirmTask) return;
        setCompletingTask(true);
        try {
            await completeWorkflowTask(confirmTask.id);
            toast.success('Task marked as done');
            setConfirmTask(null);
            await Promise.all([loadTasks(), loadOverview()]);
        } catch (error) {
            console.error('Error completing task:', error);
            toast.error('Failed to complete task');
        } finally {
            setCompletingTask(false);
        }
    };

    const handleCompleteAllTasks = async () => {
        setCompletingAllTasks(true);
        try {
            const result = await completeAllWorkflowTasks(affiliateId);
            const count = result?.completedCount || 0;
            toast.success(count === 1 ? '1 task marked as done' : `${count} tasks marked as done`);
            setConfirmCompleteAll(false);
            await Promise.all([loadTasks(), loadOverview()]);
        } catch (error) {
            console.error('Error completing all tasks:', error);
            toast.error('Failed to mark all tasks as done');
        } finally {
            setCompletingAllTasks(false);
        }
    };

    // ---- Rule dialog helpers ----

    const openCreateRuleDialog = (preset = null) => {
        if (preset) {
            setRuleForm({ ...emptyRuleForm, ...preset, params: { ...preset.params } });
        } else {
            setRuleForm(emptyRuleForm);
        }
        setRuleDialogOpen(true);
    };

    const openEditRuleDialog = (rule) => {
        setRuleForm({
            id: rule.id,
            type: rule.type,
            name: rule.name,
            priority: rule.priority,
            params: { ...(rule.params || {}) },
        });
        setRuleDialogOpen(true);
    };

    const handleRuleTypeChange = (type) => {
        const typeDef = ruleTypeMap[type];
        setRuleForm((prev) => ({
            ...emptyRuleForm,
            id: prev.id,
            type,
            name: typeDef?.label || '',
            priority: prev.priority || 'medium',
            params: {},
        }));
    };

    const handleRuleParamChange = (key, value) => {
        setRuleForm((prev) => ({
            ...prev,
            params: { ...prev.params, [key]: value },
        }));
    };

    const ruleFormValid = useMemo(() => {
        if (!ruleForm.type || !ruleForm.name.trim()) return false;
        const typeDef = ruleTypeMap[ruleForm.type];
        if (!typeDef) return false;
        return typeDef.params.every((key) => Number(ruleForm.params[key]) > 0);
    }, [ruleForm, ruleTypeMap]);

    const handleSaveRule = async () => {
        if (!ruleFormValid) return;
        setSavingRule(true);
        const payload = {
            type: ruleForm.type,
            name: ruleForm.name.trim(),
            priority: ruleForm.priority,
            params: ruleForm.params,
        };
        try {
            if (ruleForm.id) {
                await updateWorkflowRule(ruleForm.id, payload);
                toast.success('Rule updated');
            } else {
                await createWorkflowRule(affiliateId, payload);
                toast.success('Rule created');
            }
            setRuleDialogOpen(false);
            await loadOverview();
        } catch (error) {
            console.error('Error saving rule:', error);
            toast.error(error.response?.data?.error || 'Failed to save rule');
        } finally {
            setSavingRule(false);
        }
    };

    const handleToggleRule = async (rule) => {
        try {
            await updateWorkflowRule(rule.id, { active: !rule.active });
            setRules((prev) =>
                prev.map((r) => (r.id === rule.id ? { ...r, active: !rule.active } : r))
            );
        } catch (error) {
            console.error('Error toggling rule:', error);
            toast.error('Failed to update rule');
        }
    };

    const handleDeleteRule = async () => {
        if (!deleteRuleTarget) return;
        setDeletingRule(true);
        try {
            await deleteWorkflowRule(deleteRuleTarget.id);
            toast.success('Rule deleted');
            setDeleteRuleTarget(null);
            await Promise.all([loadOverview(), loadTasks()]);
        } catch (error) {
            console.error('Error deleting rule:', error);
            toast.error('Failed to delete rule');
        } finally {
            setDeletingRule(false);
        }
    };

    // ---- Derived data ----

    const filteredTasks = useMemo(() => {
        let list = [...tasks];
        if (priorityFilter !== 'all') {
            list = list.filter((t) => t.priority === priorityFilter);
        }
        if (ruleFilter !== 'all') {
            list = list.filter((t) => t.rule?.id === Number(ruleFilter));
        }
        if (!showCompleted) {
            list.sort(
                (a, b) =>
                    (PRIORITY_WEIGHT[a.priority] ?? 1) - (PRIORITY_WEIGHT[b.priority] ?? 1) ||
                    new Date(b.createdAt) - new Date(a.createdAt)
            );
        }
        return list;
    }, [tasks, priorityFilter, ruleFilter, showCompleted]);

    const describeRuleParams = (rule) => {
        const typeDef = ruleTypeMap[rule.type];
        const parts = [];
        for (const [key, value] of Object.entries(rule.params || {})) {
            if (key === 'planId') {
                const plan = plans.find((p) => p.id === Number(value));
                parts.push(`Plan: ${plan ? plan.name : `#${value}`}`);
            } else {
                parts.push(`${PARAM_LABELS[key] || key}: ${value}`);
            }
        }
        return parts.length > 0 ? parts.join(' · ') : typeDef?.label || rule.type;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Workflow BETA
                    </Typography>
                    <Chip
                        label={`${pendingCount} task${pendingCount === 1 ? '' : 's'}`}
                        color="primary"
                        size="small"
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={workflowActive}
                            onChange={handleToggleWorkflow}
                            color="primary"
                        />
                    }
                    label="Workflow active"
                />
            </Box>

            {!workflowActive && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Workflow is inactive. No new tasks will be generated, even for rules
                    that are switched on.
                </Alert>
            )}

            <Tabs
                value={activeTab}
                onChange={(e, value) => setActiveTab(value)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Tasks" />
                <Tab label="Rules" />
            </Tabs>

            {/* Content grays out when the workflow master switch is off */}
            <Box sx={{ opacity: workflowActive ? 1 : 0.55 }}>
                {activeTab === 0 && (
                    <Box>
                        {/* Filters */}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            sx={{ mb: 2, alignItems: { sm: 'center' } }}
                        >
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={priorityFilter}
                                    label="Priority"
                                    onChange={(e) => setPriorityFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All</MenuItem>
                                    {PRIORITY_OPTIONS.map((p) => (
                                        <MenuItem key={p.value} value={p.value}>
                                            {p.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Rule</InputLabel>
                                <Select
                                    value={ruleFilter}
                                    label="Rule"
                                    onChange={(e) => setRuleFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All rules</MenuItem>
                                    {rules.map((rule) => (
                                        <MenuItem key={rule.id} value={String(rule.id)}>
                                            {rule.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={showCompleted}
                                        onChange={handleShowCompletedToggle}
                                    />
                                }
                                label="Completed"
                            />
                            {!showCompleted && pendingCount > 0 && (
                                <Button
                                    variant="outlined"
                                    onClick={() => setConfirmCompleteAll(true)}
                                    sx={{ ml: { sm: 'auto' } }}
                                >
                                    Mark all as done
                                </Button>
                            )}
                        </Stack>

                        {tasksLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : filteredTasks.length === 0 ? (
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <AssignmentTurnedInIcon
                                        sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                                    />
                                    <Typography variant="h6" gutterBottom>
                                        {showCompleted
                                            ? 'No completed tasks yet'
                                            : 'No open tasks'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {rules.length === 0
                                            ? 'Create rules in the Rules tab and the system will generate tasks every night at 04:10.'
                                            : 'The system generates new tasks every night at 04:10 based on your rules.'}
                                    </Typography>
                                    {rules.length === 0 && (
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            sx={{ mt: 2 }}
                                            onClick={() => setActiveTab(1)}
                                        >
                                            Set up rules
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Stack spacing={2}>
                                {filteredTasks.map((task) => (
                                    <Card key={task.id} variant="outlined">
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            cursor: task.user?.id ? 'pointer' : 'default',
                                                            color: 'primary.main',
                                                            '&:hover': task.user?.id
                                                                ? { textDecoration: 'underline' }
                                                                : {},
                                                        }}
                                                        onClick={
                                                            task.user?.id
                                                                ? () => handleOpenProfile(task.user.id)
                                                                : undefined
                                                        }
                                                    >
                                                        {task.user?.fullName || task.user?.email}
                                                    </Typography>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{
                                                            alignItems: 'center',
                                                            flexWrap: 'wrap',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {task.user?.email && (
                                                            <Typography variant="body2">
                                                                {task.user.email}
                                                            </Typography>
                                                        )}
                                                        {task.user?.phone && (
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.25,
                                                                }}
                                                            >
                                                                <PhoneIcon sx={{ fontSize: 16 }} />
                                                                <Typography variant="body2">
                                                                    {task.user.phone}
                                                                </Typography>
                                                                <Tooltip title="Copy phone number">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleCopyPhone(
                                                                                task.user.phone
                                                                            )
                                                                        }
                                                                    >
                                                                        <ContentCopyIcon
                                                                            sx={{ fontSize: 16 }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Box>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{ alignItems: 'center' }}
                                                >
                                                    {task.rule?.name && (
                                                        <Chip
                                                            label={task.rule.name}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                    <Chip
                                                        label={
                                                            PRIORITY_OPTIONS.find(
                                                                (p) => p.value === task.priority
                                                            )?.label || task.priority
                                                        }
                                                        color={
                                                            PRIORITY_COLORS[task.priority] ||
                                                            'default'
                                                        }
                                                        size="small"
                                                    />
                                                </Stack>
                                            </Box>

                                            <Typography variant="body1" sx={{ mt: 1.5 }}>
                                                {task.description || task.title}
                                            </Typography>

                                            <Divider sx={{ my: 1.5 }} />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Created: {formatDate(task.createdAt)}
                                                    {task.dueDate &&
                                                        ` · Due: ${formatDate(task.dueDate)}`}
                                                    {showCompleted &&
                                                        task.completedAt &&
                                                        ` · Completed: ${formatDate(task.completedAt)}`}
                                                </Typography>
                                                {!showCompleted && (
                                                    <Stack direction="row" spacing={1}>
                                                        {task.user?.email && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<EmailIcon />}
                                                                onClick={() => setEmailTask(task)}
                                                            >
                                                                Send email
                                                            </Button>
                                                        )}
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => setConfirmTask(task)}
                                                        >
                                                            Mark as done
                                                        </Button>
                                                    </Stack>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Box>
                )}

                {activeTab === 1 && (
                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                mb: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => openCreateRuleDialog()}
                            >
                                Add rule
                            </Button>
                        </Box>

                        {rules.length === 0 ? (
                            <Card variant="outlined">
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Start here
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        Rules tell the system which tasks to generate for you
                                        every night at 04:10. Start with one of these:
                                    </Typography>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1}
                                        sx={{ justifyContent: 'center' }}
                                    >
                                        {SUGGESTED_RULES.map((suggestion) => (
                                            <Button
                                                key={suggestion.type}
                                                variant="outlined"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => openCreateRuleDialog(suggestion)}
                                            >
                                                {suggestion.name}
                                            </Button>
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        ) : (
                            <Stack spacing={1.5}>
                                {rules.map((rule) => (
                                    <Card key={rule.id} variant="outlined">
                                        <CardContent
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                '&:last-child': { pb: 2 },
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 'bold' }}
                                                    >
                                                        {rule.name}
                                                    </Typography>
                                                    <Chip
                                                        label={
                                                            PRIORITY_OPTIONS.find(
                                                                (p) => p.value === rule.priority
                                                            )?.label || rule.priority
                                                        }
                                                        color={
                                                            PRIORITY_COLORS[rule.priority] ||
                                                            'default'
                                                        }
                                                        size="small"
                                                    />
                                                </Box>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {ruleTypeMap[rule.type]?.label || rule.type} ·{' '}
                                                    {describeRuleParams(rule)}
                                                </Typography>
                                            </Box>
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                sx={{ alignItems: 'center' }}
                                            >
                                                <Tooltip
                                                    title={rule.active ? 'Rule is on' : 'Rule is off'}
                                                >
                                                    <Switch
                                                        checked={rule.active}
                                                        onChange={() => handleToggleRule(rule)}
                                                        size="small"
                                                    />
                                                </Tooltip>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openEditRuleDialog(rule)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => setDeleteRuleTarget(rule)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Box>
                )}
            </Box>

            <Dialog
                open={confirmCompleteAll}
                onClose={() => !completingAllTasks && setConfirmCompleteAll(false)}
            >
                <DialogTitle>Mark all tasks as done?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Are you sure you want to mark all ${pendingCount} open task${
                            pendingCount === 1 ? '' : 's'
                        } as done? This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmCompleteAll(false)}
                        disabled={completingAllTasks}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCompleteAllTasks}
                        disabled={completingAllTasks}
                    >
                        {completingAllTasks ? <CircularProgress size={20} /> : 'Mark all as done'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm complete dialog */}
            <Dialog open={Boolean(confirmTask)} onClose={() => setConfirmTask(null)}>
                <DialogTitle>Mark task as done?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmTask &&
                            `Are you sure you want to mark the task for ${
                                confirmTask.user?.fullName || confirmTask.user?.email
                            } as done? This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmTask(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCompleteTask}
                        disabled={completingTask}
                    >
                        {completingTask ? <CircularProgress size={20} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm rule delete dialog */}
            <Dialog open={Boolean(deleteRuleTarget)} onClose={() => setDeleteRuleTarget(null)}>
                <DialogTitle>Delete rule?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteRuleTarget &&
                            `Deleting "${deleteRuleTarget.name}" will also remove all tasks generated by this rule. This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteRuleTarget(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteRule}
                        disabled={deletingRule}
                    >
                        {deletingRule ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rule create/edit dialog */}
            <Dialog
                open={ruleDialogOpen}
                onClose={() => setRuleDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{ruleForm.id ? 'Edit rule' : 'Add rule'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth disabled={Boolean(ruleForm.id)}>
                            <InputLabel>Rule type</InputLabel>
                            <Select
                                value={ruleForm.type}
                                label="Rule type"
                                onChange={(e) => handleRuleTypeChange(e.target.value)}
                            >
                                {ruleTypes.map((rt) => (
                                    <MenuItem key={rt.type} value={rt.type}>
                                        {rt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {ruleForm.type && ruleTypeMap[ruleForm.type] && (
                            <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                                {ruleTypeMap[ruleForm.type].description}
                            </Alert>
                        )}
                        <TextField
                            label="Rule name"
                            value={ruleForm.name}
                            onChange={(e) =>
                                setRuleForm((prev) => ({ ...prev, name: e.target.value }))
                            }
                            fullWidth
                        />
                        {ruleForm.type &&
                            (ruleTypeMap[ruleForm.type]?.params || []).map((key) =>
                                key === 'planId' ? (
                                    <FormControl
                                        fullWidth
                                        key={`${ruleForm.type}-${key}`}
                                    >
                                        <InputLabel>{PARAM_LABELS.planId}</InputLabel>
                                        <Select
                                            value={ruleForm.params.planId || ''}
                                            label={PARAM_LABELS.planId}
                                            onChange={(e) =>
                                                handleRuleParamChange('planId', e.target.value)
                                            }
                                        >
                                            {plans.map((plan) => (
                                                <MenuItem key={plan.id} value={plan.id}>
                                                    {plan.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <TextField
                                        key={`${ruleForm.type}-${key}`}
                                        label={PARAM_LABELS[key] || key}
                                        type="number"
                                        value={ruleForm.params[key] ?? ''}
                                        onChange={(e) =>
                                            handleRuleParamChange(key, e.target.value)
                                        }
                                        slotProps={{ htmlInput: { min: 1 } }}
                                        fullWidth
                                    />
                                )
                            )}
                        <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={ruleForm.priority}
                                label="Priority"
                                onChange={(e) =>
                                    setRuleForm((prev) => ({
                                        ...prev,
                                        priority: e.target.value,
                                    }))
                                }
                            >
                                {PRIORITY_OPTIONS.map((p) => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveRule}
                        disabled={!ruleFormValid || savingRule}
                    >
                        {savingRule ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ProfileModal
                open={profileOpen}
                onClose={() => {
                    setProfileOpen(false);
                    setSelectedUser(null);
                }}
                user={selectedUser}
                affiliateId={affiliateId}
                affiliateEmail={affiliateEmail}
                onUserUpdated={handleUserUpdated}
            />

            {/* Send email through IronTrack */}
            {emailTask && (
                <SendMessageModal
                    open={Boolean(emailTask)}
                    onClose={() => setEmailTask(null)}
                    affiliate={affiliateId}
                    affiliateEmail={affiliateEmail}
                    preSelectedUsers={[
                        {
                            id: emailTask.user.id,
                            fullName: emailTask.user.fullName,
                            email: emailTask.user.email,
                        },
                    ]}
                    initialSubject={`${affiliate?.name || 'Your gym'} - checking in`}
                    initialBody={`Hi ${emailTask.user.fullName || ''},\n\n`}
                />
            )}
        </Box>
    );
}
